import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Palette as PaletteIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import apiClient from '../../lib/api';

const DesignerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [errors, setErrors] = useState({});
  const [stats, setStats] = useState({
    totalCards: 0,
    publishedCards: 0,
  });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isActive: true,
    stripeConnectAccount: '',
  });
  const [company, setCompany] = useState(null);
  const [stripeAccountDetails, setStripeAccountDetails] = useState(null);
  const [loadingStripeAccount, setLoadingStripeAccount] = useState(false);
  const [stripeModalOpen, setStripeModalOpen] = useState(false);
  const [stripeAccountIdInput, setStripeAccountIdInput] = useState('');
  const [savingStripeAccount, setSavingStripeAccount] = useState(false);
  const [modalAccountDetails, setModalAccountDetails] = useState(null);
  const [loadingModalAccount, setLoadingModalAccount] = useState(false);

  useEffect(() => {
    if (id) {
      fetchDesigner();
      fetchStats();
    }
  }, [id]);

  const fetchDesigner = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getDesigner(id);
      // Backend returns { user: ... }
      const designer = response.user || response.designer || response.data;

      setFormData({
        firstName: designer.firstName || '',
        lastName: designer.lastName || '',
        email: designer.email || '',
        phone: designer.phone || '',
        isActive: designer.isActive !== false,
        updatedAt: designer.updatedAt || '',
        // NOTE: Do NOT set stripeConnectAccount from designer - it should only be on company
        stripeConnectAccount: '',
      });

      // Fetch company if designer has companyId
      if (designer.companyId) {
        try {
          const companyResponse = await apiClient.getCompany(designer.companyId);
          const companyData = companyResponse.company || companyResponse.data || companyResponse;
          setCompany(companyData);
          
          // ONLY use company's Stripe Connect account (no fallback to designer's)
          const stripeAccountId = companyData?.stripeConnectAccount;
          if (stripeAccountId) {
            fetchStripeAccountDetails(stripeAccountId);
          } else {
            setStripeAccountDetails(null);
          }
        } catch (error) {
          console.error('Error fetching company:', error);
          setStripeAccountDetails(null);
        }
      } else {
        // No company - designer should not have Stripe account
        setStripeAccountDetails(null);
      }
    } catch (error) {
      console.error('Error fetching designer:', error);
      toast.error('Error fetching designer');
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeAccountDetails = async (accountId) => {
    if (!accountId) {
      setStripeAccountDetails(null);
      return;
    }

    setLoadingStripeAccount(true);
    try {
      const response = await apiClient.getStripeConnectAccount(accountId);
      setStripeAccountDetails(response.data || response);
    } catch (error) {
      console.error('Error fetching Stripe account details:', error);
      setStripeAccountDetails(null);
    } finally {
      setLoadingStripeAccount(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.getCardStats(id);
      if (response.success && response.data) {
        const cardsByStatus = response.data.cardsByStatus || {};
        setStats({
          totalCards: response.data.totalCards || 0,
          publishedCards: cardsByStatus.published || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Don't show error toast for stats, just use defaults
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      // Don't include stripeConnectAccount in the main form update
      const { stripeConnectAccount, ...updateData } = formData;
      await apiClient.updateDesigner(id, updateData);
      toast.success('Designer updated successfully');
    } catch (error) {
      console.error('Error updating designer:', error);
      const errorMessage = error.response?.data?.error || 'Error updating designer';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenStripeModal = () => {
    // Use company's Stripe account ID, not designer's
    setStripeAccountIdInput(company?.stripeConnectAccount || '');
    setModalAccountDetails(null);
    setStripeModalOpen(true);
  };

  const handleCloseStripeModal = () => {
    setStripeModalOpen(false);
    setStripeAccountIdInput('');
    setModalAccountDetails(null);
  };

  const handleSaveStripeAccount = async () => {
    setSavingStripeAccount(true);
    try {
      // Update company's Stripe Connect account, not designer's
      if (company?.id) {
        await apiClient.updateCompany(company.id, { stripeConnectAccount: stripeAccountIdInput });
        toast.success('Company Stripe Connect account updated successfully');
        
        // Refresh company data
        const companyResponse = await apiClient.getCompany(company.id);
        const updatedCompany = companyResponse.company || companyResponse.data || companyResponse;
        setCompany(updatedCompany);
        
        if (stripeAccountIdInput) {
          await fetchStripeAccountDetails(stripeAccountIdInput);
        } else {
          setStripeAccountDetails(null);
        }
      } else {
        toast.error('Designer must be associated with a company to set Stripe Connect account');
      }
      
      handleCloseStripeModal();
    } catch (error) {
      console.error('Error updating Stripe account:', error);
      const errorMessage = error.response?.data?.error || 'Error updating Stripe Connect account';
      toast.error(errorMessage);
    } finally {
      setSavingStripeAccount(false);
    }
  };

  const handleBack = () => {
    navigate('/designers');
  };

  const handleSendInvite = async () => {
    setSendingInvite(true);
    try {
      await apiClient.sendDesignerInvite(id, {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      toast.success('Invite sent successfully');
    } catch (error) {
      console.error('Error sending invite:', error);
      const errorMessage = error.response?.data?.error || 'Error sending invite';
      toast.error(errorMessage);
    } finally {
      setSendingInvite(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Loading Designer">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading designer...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  if (!id) {
    return (
      <DashboardLayout title="Designer Not Found">
        <Alert severity="error">Designer ID not found</Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Edit Designer">
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h4" component="h1">
                Edit Designer
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Update the designer information below
              </Typography>
            </Box>
            
            <Button
              variant="outlined"
              onClick={handleSendInvite}
              disabled={sendingInvite || !formData.email}
              startIcon={sendingInvite ? null : <EmailIcon />}
              sx={{ 
                minWidth: 150,
                borderColor: '#2c4575',
                color: '#2c4575',
                '&:hover': {
                  borderColor: '#1e3154',
                  backgroundColor: 'rgba(44, 69, 117, 0.04)'
                }
              }}
            >
              {sendingInvite ? 'Sending...' : 'Send Invite'}
            </Button>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
            gap: 2 
          }}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3,
              },
              transition: 'all 0.2s ease-in-out',
            }}
            onClick={() => navigate(`/cards?designerId=${id}`)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PaletteIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Cards
                  </Typography>
                </Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  {stats.totalCards}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
                  Click to view all cards
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Published Cards
                  </Typography>
                </Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  {stats.publishedCards}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
                  Cards available in store
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ 
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white',
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Revenue
                  </Typography>
                </Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  N/A
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
                  Revenue data not available
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 3 
              }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  required
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Optional phone number"
                  sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
                />
                <FormControl fullWidth sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.isActive ? 'active' : 'inactive'}
                    onChange={(e) => handleInputChange('isActive', e.target.value === 'active')}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end',
                mt: 4,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                  sx={{ 
                    minWidth: 200
                  }}
                >
                  Back to Designers
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  startIcon={saving ? null : <CheckIcon />}
                  sx={{ 
                    minWidth: 200
                  }}
                >
                  {saving ? 'Updating...' : 'Update Designer'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>

        {/* Stripe Connect Account Card */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon />
                <Typography variant="h6">Stripe Connect Account</Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={handleOpenStripeModal}
                startIcon={<CreditCardIcon />}
                sx={{ 
                  borderColor: '#2c4575',
                  color: '#2c4575',
                  '&:hover': {
                    borderColor: '#1e3154',
                    backgroundColor: 'rgba(44, 69, 117, 0.04)'
                  }
                }}
              >
                {company?.stripeConnectAccount ? 'View Account' : 'Set Account'}
              </Button>
            </Box>

            {company?.stripeConnectAccount ? (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Account ID:
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {company.stripeConnectAccount}
                  </Typography>
                  {company && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      From company: {company.companyName || company.id}
                    </Typography>
                  )}
                </Box>

                {loadingStripeAccount ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading account details...</Typography>
                  </Box>
                ) : stripeAccountDetails ? (
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Account Status
                      </Typography>
                      <Chip 
                        label={
                          stripeAccountDetails.details_submitted && 
                          stripeAccountDetails.charges_enabled && 
                          stripeAccountDetails.payouts_enabled 
                            ? 'Verified' 
                            : 'Pending'
                        }
                        size="small"
                        color={
                          stripeAccountDetails.details_submitted && 
                          stripeAccountDetails.charges_enabled && 
                          stripeAccountDetails.payouts_enabled 
                            ? 'success' 
                            : 'warning'
                        }
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Details Submitted:
                        </Typography>
                        <Typography variant="body2">
                          {stripeAccountDetails.details_submitted ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Charges Enabled:
                        </Typography>
                        <Typography variant="body2">
                          {stripeAccountDetails.charges_enabled ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Payouts Enabled:
                        </Typography>
                        <Typography variant="body2">
                          {stripeAccountDetails.payouts_enabled ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                      {stripeAccountDetails.business_type && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            Business Type:
                          </Typography>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {stripeAccountDetails.business_type}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="warning">
                    Unable to load account details. Please verify the account ID is correct.
                  </Alert>
                )}
              </Box>
            ) : (
              <Alert severity="info">
                No Stripe Connect account configured. Click "Set Account" to add one.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Stripe Connect Account Modal */}
      <Dialog
        open={stripeModalOpen}
        onClose={handleCloseStripeModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon />
            <Typography variant="h6">Stripe Connect Account</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              Changing the Stripe Connect account is a significant action. This will affect how payouts are processed for this designer.
            </Alert>
            <TextField
              fullWidth
              label="Stripe Connect Account ID"
              value={stripeAccountIdInput}
              onChange={(e) => setStripeAccountIdInput(e.target.value)}
              placeholder="acct_..."
              helperText="Enter the Stripe Connect account ID (e.g., acct_1234567890). Leave empty to remove the account."
              sx={{ mb: 2 }}
            />
            {stripeAccountIdInput && (
              <Button
                variant="outlined"
                onClick={async () => {
                  if (stripeAccountIdInput) {
                    setLoadingModalAccount(true);
                    try {
                      const response = await apiClient.getStripeConnectAccount(stripeAccountIdInput);
                      setModalAccountDetails(response.data || response);
                    } catch (error) {
                      console.error('Error fetching Stripe account details:', error);
                      setModalAccountDetails(null);
                    } finally {
                      setLoadingModalAccount(false);
                    }
                  }
                }}
                disabled={loadingModalAccount || !stripeAccountIdInput}
                fullWidth
                sx={{ mb: 2 }}
              >
                {loadingModalAccount ? 'Loading...' : 'Load Account Details'}
              </Button>
            )}
            {stripeAccountIdInput && loadingModalAccount && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading account details...</Typography>
              </Box>
            )}
            {stripeAccountIdInput && !loadingModalAccount && modalAccountDetails && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                {/* Business Name */}
                {(modalAccountDetails.company?.name || (modalAccountDetails.individual?.first_name && modalAccountDetails.individual?.last_name)) && (
                  <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Business Name:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {modalAccountDetails.company?.name || 
                       `${modalAccountDetails.individual?.first_name || ''} ${modalAccountDetails.individual?.last_name || ''}`.trim()}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Account Status
                  </Typography>
                  <Chip 
                    label={
                      modalAccountDetails.details_submitted && 
                      modalAccountDetails.charges_enabled && 
                      modalAccountDetails.payouts_enabled 
                        ? 'Verified' 
                        : 'Pending'
                    }
                    size="small"
                    color={
                      modalAccountDetails.details_submitted && 
                      modalAccountDetails.charges_enabled && 
                      modalAccountDetails.payouts_enabled 
                        ? 'success' 
                        : 'warning'
                    }
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Details Submitted:
                    </Typography>
                    <Typography variant="body2">
                      {modalAccountDetails.details_submitted ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Charges Enabled:
                    </Typography>
                    <Typography variant="body2">
                      {modalAccountDetails.charges_enabled ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Payouts Enabled:
                    </Typography>
                    <Typography variant="body2">
                      {modalAccountDetails.payouts_enabled ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                  {modalAccountDetails.business_type && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Business Type:
                      </Typography>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {modalAccountDetails.business_type}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {stripeAccountIdInput && !loadingModalAccount && !modalAccountDetails && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Unable to load account details. Please verify the account ID is correct.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStripeModal} disabled={savingStripeAccount}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveStripeAccount}
            variant="contained"
            disabled={savingStripeAccount}
          >
            {savingStripeAccount ? 'Saving...' : 'Save Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default DesignerDetail;
